/**
 * 통합관리자 — 과금 관리
 * ---------------------------------------------------------------------------
 * 운영자가 매달 하는 일을 한 화면에 모은다.
 *   미수금 확인 → 청구서 발행 → 입금 확인 → (필요하면) 할인·환불
 *
 * 청구서 발행 전에 금액을 먼저 보여준다. 발행하고 나서 틀린 걸 알면
 * 무효화하고 다시 만들어야 하는데, 고객에게는 이미 나간 뒤다.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { downloadCsv, csvDate } from '../../lib/csv';
import { BatchInvoicePanel } from './BatchInvoicePanel';
import { RefundPanel } from './RefundPanel';
import { TaxInvoicePanel } from './TaxInvoicePanel';
import { TenantLedgerDrawer } from './TenantLedgerDrawer';

const { Text, Paragraph } = Typography;

interface SubscriptionRow {
  subscription_id: string;
  tenant_id: string;
  product_code: string;
  product_name: string;
  billing_unit: 'FLAT' | 'PER_USER' | 'PER_SITE' | 'PER_UNIT';
  unit_label: string | null;
  included_units: number;
  company_name: string;
  slug: string;
  status: string;
  units: number;
  active_users: number;
  plan_code: string;
  plan_name: string;
  base_price: number;
  unit_price: number;
  current_period_start: string;
  current_period_end: string;
  /** 체험이 끝나는 시각. 비어 있으면 체험이 아니다 */
  trial_ends_at: string | null;
  overdue_amount: number;
  /** 이 구독에서 매달 나오는 금액(공급가액). 엑셀로 내보낼 때 쓴다 */
  monthly_amount: number;
  /**
   * 지금 걸려 있는 할인. 비율과 정액이 **함께** 걸릴 수 있어 배열이다.
   * 없으면 null 로 온다(jsonb_agg 는 빈 결과에 null 을 준다).
   */
  discounts: SubscriptionDiscount[] | null;
}

/** 요금제 고르는 칸에 쓸 최소 정보. `/admin/plans` 응답의 일부다. */
interface PlanOption {
  plan_id: string;
  product_code: string;
  product_name: string;
  plan_code: string;
  plan_name: string;
  billing_unit: string;
  unit_label: string | null;
  included_units: number;
  base_price: number;
  unit_price: number;
  min_units: number;
  max_units: number | null;
  is_active: boolean;
}

interface SubscriptionDiscount {
  type: 'DISCOUNT_RATE' | 'DISCOUNT_AMOUNT';
  /** 비율은 0~1, 정액은 원 */
  value: number;
  reason: string;
  validFrom: string;
  validUntil: string | null;
}

/** 할인 한 줄을 사람이 읽는 말로. 비율은 %로 되돌려 보여 준다. */
function discountLabel(d: SubscriptionDiscount): string {
  return d.type === 'DISCOUNT_RATE'
    ? `${(d.value * 100).toFixed(d.value * 100 % 1 === 0 ? 0 : 1)}%`
    : `${Math.round(d.value).toLocaleString('ko-KR')}원`;
}

/**
 * 체험이 며칠 남았나. 끝났으면 0, 체험이 아니면 null.
 *
 * 날짜만 비교한다. 시·분까지 보면 같은 날인데 「0일 남음」과 「1일 남음」이
 * 갈려서, 보는 사람마다 다른 숫자를 말하게 된다.
 */
function trialDaysLeft(trialEndsAt: string | null): number | null {
  if (!trialEndsAt) return null;
  const end = new Date(trialEndsAt);
  if (Number.isNaN(end.getTime())) return null;

  const dayMs = 24 * 60 * 60 * 1000;
  const endDay = Math.floor(end.getTime() / dayMs);
  const today = Math.floor(Date.now() / dayMs);
  return Math.max(0, endDay - today);
}

interface InvoiceRow {
  invoice_id: string;
  tenant_id: string;
  invoice_no: string;
  status: string;
  company_name: string;
  slug: string;
  period_start: string;
  period_end: string;
  issued_at: string | null;
  due_at: string | null;
  subtotal_amount: number;
  vat_amount: number;
  /** 환불 차감액. total_amount 는 이것을 뺀 뒤의 실제 청구 금액이다 */
  credit_amount: number;
  total_amount: number;
  paid_amount: number;
  /** 배치가 자동으로 발행했는가 */
  issued_by_system: boolean;
}

interface PreviewLine {
  itemType: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

interface Preview {
  companyName: string;
  periodStart: string;
  periodEnd: string;
  lines: PreviewLine[];
  subtotalAmount: number;
  discountAmount: number;
  overageAmount: number;
  vatAmount: number;
  /** 아직 못 갚은 환불 중 이번에 깎을 금액. 부가세까지 더한 뒤에 뺀다 */
  creditAmount: number;
  totalAmount: number;
  /** 이 기간과 겹치는 청구서가 이미 있으면 그 청구서. 없으면 null */
  existingInvoice: {
    invoiceNo: string;
    status: string;
    periodStart: string;
    periodEnd: string;
    totalAmount: number;
    issuedAt: string | null;
  } | null;
}

const INVOICE_STATUS: Record<string, { text: string; color: string }> = {
  DRAFT: { text: '작성중', color: 'default' },
  ISSUED: { text: '발행', color: 'blue' },
  // 일부만 들어온 청구서. 남은 금액은 미수금에 그대로 잡히고 자동 결제
  // 대상에도 남는다. 이 줄이 없으면 화면에 'PARTIALLY_PAID' 가 그대로 찍힌다.
  PARTIALLY_PAID: { text: '부분수납', color: 'gold' },
  PAID: { text: '수납완료', color: 'green' },
  OVERDUE: { text: '연체', color: 'red' },
  VOID: { text: '무효', color: 'default' },
  REFUNDED: { text: '환불', color: 'orange' },
};

const won = (v: number | null | undefined) => `${Number(v ?? 0).toLocaleString('ko-KR')}원`;

export default function BillingPage() {
  const { message } = AntdApp.useApp();
  const client = useQueryClient();

  const [previewTarget, setPreviewTarget] = useState<SubscriptionRow | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  /*
   * 계산이 실패한 이유를 창 안에 둔다.
   *
   * 예전에는 토스트만 띄우고 창을 닫았다. 「구독 정보가 없어 청구서를 만들 수
   * 없습니다」가 위에 떴다 사라지는데, 화면에는 구독이 버젓이 한 줄 있으니
   * 무엇을 해야 할지 알 수 없다. 창을 열어 둔 채 이유를 보여 주고 기간을
   * 바꿔 다시 계산할 수 있게 한다.
   */
  const [previewError, setPreviewError] = useState<string | null>(null);
  /** 청구 대상 월. 기본은 지난달이지만 이번 달에 가입했으면 지난달엔 아무것도 없다. */
  const [billingMonth, setBillingMonth] = useState<Dayjs>(dayjs().subtract(1, 'month'));
  const [issuing, setIssuing] = useState(false);
  /*
   * 조회 조건.
   *
   * 구독과 청구서가 보는 날짜가 다르다 — 구독은 시작일·체험 종료·다음 청구,
   * 청구서는 이용 기간·발행일·납기일이다. 하나로 합치면 어느 쪽에서도
   * 맞지 않는 목록이 나온다. 그래서 둘을 따로 둔다.
   */
  const [subFilter, setSubFilter] = useState<{
    dateField: 'STARTED' | 'TRIAL_END' | 'NEXT_BILLING';
    range: [Dayjs, Dayjs] | null;
    keyword: string;
    status?: string;
    overdueOnly: boolean;
  }>({ dateField: 'STARTED', range: null, keyword: '', overdueOnly: false });

  const [invFilter, setInvFilter] = useState<{
    dateField: 'PERIOD' | 'ISSUED' | 'DUE';
    range: [Dayjs, Dayjs] | null;
    keyword: string;
    status?: string;
    unpaidOnly: boolean;
  }>({ dateField: 'PERIOD', range: null, keyword: '', unpaidOnly: false });

  const [payTarget, setPayTarget] = useState<InvoiceRow | null>(null);
  const [discountTarget, setDiscountTarget] = useState<SubscriptionRow | null>(null);
  const [unitsTarget, setUnitsTarget] = useState<SubscriptionRow | null>(null);
  const [statusTarget, setStatusTarget] = useState<SubscriptionRow | null>(null);
  const [planTarget, setPlanTarget] = useState<SubscriptionRow | null>(null);
  const [subscribeOpen, setSubscribeOpen] = useState(false);

  const [payForm] = Form.useForm();
  const [discountForm] = Form.useForm();
  const [unitsForm] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [planForm] = Form.useForm();
  const [subscribeForm] = Form.useForm();

  /*
   * 거래 원장 서랍. 어느 탭에서든 기업 이름을 누르면 그 자리에서 열린다 —
   * 이 화면이 필요해지는 순간은 늘 다른 목록을 보다가이기 때문이다.
   */
  const [ledgerTenant, setLedgerTenant] = useState<string | null>(null);

  const summary = useQuery<Record<string, number>, NormalizedError>({
    queryKey: ['admin', 'billing', 'summary'],
    queryFn: async () => (await apiClient.get('/admin/billing/summary')).data,
  });

  const subParams = {
    dateField: subFilter.dateField,
    from: subFilter.range?.[0]?.format('YYYY-MM-DD'),
    to: subFilter.range?.[1]?.format('YYYY-MM-DD'),
    keyword: subFilter.keyword.trim() || undefined,
    status: subFilter.status,
    overdueOnly: subFilter.overdueOnly ? 'true' : undefined,
  };

  const subscriptions = useQuery<SubscriptionRow[], NormalizedError>({
    queryKey: ['admin', 'billing', 'subscriptions', subParams],
    queryFn: async () =>
      (await apiClient.get('/admin/billing/subscriptions', { params: subParams })).data,
  });

  const invParams = {
    dateField: invFilter.dateField,
    from: invFilter.range?.[0]?.format('YYYY-MM-DD'),
    to: invFilter.range?.[1]?.format('YYYY-MM-DD'),
    keyword: invFilter.keyword.trim() || undefined,
    status: invFilter.status,
    unpaidOnly: invFilter.unpaidOnly ? 'true' : undefined,
    // 엑셀로 내보낼 수 있어야 하므로 화면 쪽 상한을 넉넉히 둔다.
    limit: 2000,
  };

  const invoices = useQuery<InvoiceRow[], NormalizedError>({
    queryKey: ['admin', 'billing', 'invoices', invParams],
    queryFn: async () =>
      (await apiClient.get('/admin/billing/invoices', { params: invParams })).data,
  });

  const refresh = () => client.invalidateQueries({ queryKey: ['admin', 'billing'] });

  /*
   * 요금제 목록. 요금제 변경·신규 구독 창이 쓴다.
   *
   * 창을 열 때마다 받지 않고 한 번만 받아 둔다 — 요금제는 자주 바뀌지 않고,
   * 창이 열리는 순간 고를 것이 없으면 운영자는 목록이 비었다고 생각한다.
   */
  const plans = useQuery<PlanOption[], NormalizedError>({
    queryKey: ['admin', 'billing', 'plan-options'],
    queryFn: async () => (await apiClient.get<PlanOption[]>('/admin/plans')).data,
    staleTime: 60_000,
  });

  /*
   * 기업 목록. 신규 구독 창에서 고른다.
   *
   * 구독 목록에서 뽑아 쓸 수는 없다 — 아직 아무 제품도 구독하지 않은 기업이
   * 빠지는데, 그 기업이야말로 여기서 구독을 걸어 줘야 할 대상이다.
   */
  const tenantOptions = useQuery<Array<{ tenantId: string; companyName: string; slug: string }>, NormalizedError>({
    queryKey: ['admin', 'billing', 'tenant-options'],
    queryFn: async () => (await apiClient.get('/admin/tenants')).data,
    staleTime: 60_000,
  });

  /** 그 제품에 **지금 팔고 있는** 요금제만. 판매중지된 것으로는 바꿀 수 없다. */
  const plansFor = (productCode?: string) =>
    (plans.data ?? [])
      .filter((p) => p.is_active && (!productCode || p.product_code === productCode))
      .map((p) => ({
        value: p.plan_id,
        label:
          `${p.plan_name} — 기본 ${won(p.base_price)}` +
          (p.billing_unit === 'FLAT'
            ? ' (정액)'
            : ` + ${p.unit_label ?? '단위'}당 ${won(p.unit_price)}` +
              (p.included_units > 0 ? ` (기본 ${p.included_units} 포함)` : '')),
      }));

  const loadPreview = async (tenantId: string, month: Dayjs) => {
    setPreview(null);
    setPreviewError(null);
    try {
      const { data } = await apiClient.get<Preview>(`/admin/billing/preview/${tenantId}`, {
        params: {
          periodStart: month.startOf('month').format('YYYY-MM-DD'),
          periodEnd: month.endOf('month').format('YYYY-MM-DD'),
        },
      });
      setPreview(data);
    } catch (err) {
      setPreviewError(errorText(err as NormalizedError));
    }
  };

  const openPreview = async (row: SubscriptionRow) => {
    setPreviewTarget(row);

    /*
     * 기본은 지난달이지만, 구독이 이번 달에 시작했으면 지난달에는 청구할 것이
     * 없다. 늘 "구독 정보가 없다" 로 시작하면 쓸모가 없으니 이번 달로 연다.
     */
    const started = row.current_period_start ? dayjs(row.current_period_start) : null;
    const lastMonth = dayjs().subtract(1, 'month');
    const month = started && started.isAfter(lastMonth.endOf('month')) ? dayjs() : lastMonth;

    setBillingMonth(month);
    await loadPreview(row.tenant_id, month);
  };

  const changeMonth = async (month: Dayjs | null) => {
    if (!month || !previewTarget) return;
    setBillingMonth(month);
    await loadPreview(previewTarget.tenant_id, month);
  };

  const issue = async () => {
    if (!previewTarget) return;
    setIssuing(true);
    try {
      const { data } = await apiClient.post<{ invoiceNo: string; alreadyExisted: boolean }>(
        `/admin/billing/invoices/${previewTarget.tenant_id}`,
        {
          dueDays: 14,
          periodStart: billingMonth.startOf('month').format('YYYY-MM-DD'),
          periodEnd: billingMonth.endOf('month').format('YYYY-MM-DD'),
        },
      );
      message[data.alreadyExisted ? 'info' : 'success'](
        data.alreadyExisted
          ? `이미 발행된 청구서가 있습니다 (${data.invoiceNo}).`
          : `청구서를 발행했습니다 (${data.invoiceNo}).`,
      );
      setPreviewTarget(null);
      await refresh();
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    } finally {
      setIssuing(false);
    }
  };

  const recordPayment = async () => {
    const values = await payForm.validateFields().catch(() => null);
    if (!values || !payTarget) return;

    try {
      await apiClient.post(`/admin/billing/invoices/${payTarget.invoice_id}/payments`, {
        tenantId: payTarget.tenant_id,
        ...values,
        /*
         * DatePicker 는 dayjs 객체를 준다. 그대로 보내면 서버의
         * YYYY-MM-DD 검사에 걸린다. 비워 두면 서버가 오늘로 기록한다.
         */
        paidOn: values.paidOn ? dayjs(values.paidOn).format('YYYY-MM-DD') : undefined,
      });
      message.success('수납을 기록했습니다.');
      setPayTarget(null);
      payForm.resetFields();
      await refresh();
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  const setDiscount = async () => {
    const values = await discountForm.validateFields().catch(() => null);
    if (!values || !discountTarget) return;

    try {
      await apiClient.post(`/admin/billing/subscriptions/${discountTarget.tenant_id}/${discountTarget.product_code}/discount`, {
        type: values.type,
        // 화면에서는 %로 받고 서버에는 비율로 보낸다. 0.1 을 직접 입력하게 하면 틀리기 쉽다.
        value: values.type === 'DISCOUNT_RATE' ? Number(values.value) / 100 : Number(values.value),
        reason: values.reason.trim(),
      });
      message.success('할인을 설정했습니다.');
      setDiscountTarget(null);
      discountForm.resetFields();
      await refresh();
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  /**
   * 계약 수량을 고친다.
   *
   * 청구액을 직접 바꾸는 조작이라 이 화면에 있어야 한다. 「요금제」 화면 설명이
   * 이미 "바꾸려면 구독·청구 화면에서 수량을 고칩니다" 라고 안내하는데 정작
   * 단추가 없어서, 운영자는 DB 를 고쳐 달라고 부탁할 수밖에 없었다.
   */
  const changeUnits = async () => {
    const values = await unitsForm.validateFields().catch(() => null);
    if (!values || !unitsTarget) return;

    try {
      await apiClient.patch(
        `/admin/billing/subscriptions/${unitsTarget.tenant_id}/${unitsTarget.product_code}/units`,
        { units: Number(values.units), reason: values.reason.trim() },
      );
      message.success('계약 수량을 바꿨습니다.');
      setUnitsTarget(null);
      unitsForm.resetFields();
      await refresh();
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  /**
   * 같은 제품 안에서 요금제를 바꾼다.
   *
   * 바꾼 날부터 새 요금이다. 그달 청구서에는 옛 요금제와 새 요금제가 날짜로
   * 나뉘어 두 줄로 나온다 — 서버가 조건 구간을 남기고 일할로 계산한다.
   */
  const changePlan = async () => {
    const values = await planForm.validateFields().catch(() => null);
    if (!values || !planTarget) return;

    try {
      await apiClient.patch(`/admin/billing/subscriptions/${planTarget.tenant_id}/plan`, {
        planId: values.planId,
        units: Number(values.units),
        reason: values.reason.trim(),
      });
      message.success('요금제를 바꿨습니다.');
      setPlanTarget(null);
      planForm.resetFields();
      await refresh();
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  /** 아직 안 쓰는 제품을 새로 구독시킨다. */
  const subscribeProduct = async () => {
    const values = await subscribeForm.validateFields().catch(() => null);
    if (!values) return;

    try {
      await apiClient.post(`/admin/billing/subscriptions/${values.tenantId}`, {
        planId: values.planId,
        // 비워 두면 보내지 않는다. Number(undefined) 는 NaN 이고, 서버는
        // 숫자가 아닌 값을 거절한다 — "수량을 안 적었을 뿐" 인데 저장이 막힌다.
        ...(values.units === undefined || values.units === null
          ? {}
          : { units: Number(values.units) }),
        reason: values.reason.trim(),
      });
      message.success('구독을 시작했습니다.');
      setSubscribeOpen(false);
      subscribeForm.resetFields();
      await refresh();
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  /** 구독을 멈추거나 되살리거나 해지한다. */
  const changeStatus = async () => {
    const values = await statusForm.validateFields().catch(() => null);
    if (!values || !statusTarget) return;

    try {
      await apiClient.patch(
        `/admin/billing/subscriptions/${statusTarget.tenant_id}/${statusTarget.product_code}/status`,
        {
          status: values.status,
          reason: values.reason.trim(),
          cancelAtPeriodEnd: values.status === 'CANCELED' ? Boolean(values.cancelAtPeriodEnd) : false,
        },
      );
      message.success('구독 상태를 바꿨습니다.');
      setStatusTarget(null);
      statusForm.resetFields();
      await refresh();
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  const subscriptionColumns: ColumnsType<SubscriptionRow> = [
    {
      title: '기업',
      key: 'company',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          {/* 누르면 그 기업의 청구·수납·환불이 시간순으로 열린다 */}
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto', fontWeight: 700 }}
            onClick={() => setLedgerTenant(row.tenant_id)}
          >
            {row.company_name}
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.slug}
          </Text>
        </Space>
      ),
    },
    {
      title: '제품',
      key: 'product',
      width: 150,
      render: (_, row) => <Tag color="blue">{row.product_name}</Tag>,
    },
    {
      title: '요금제',
      key: 'plan',
      width: 200,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>{row.plan_name}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            기본 {won(row.base_price)}
            {row.billing_unit !== 'FLAT' && ` + ${row.unit_label ?? '단위'}당 ${won(row.unit_price)}`}
          </Text>
        </Space>
      ),
    },
    {
      title: '계약 수량',
      key: 'units',
      width: 130,
      render: (_, row) =>
        row.billing_unit === 'FLAT' ? (
          <Text type="secondary">정액</Text>
        ) : (
          <Space direction="vertical" size={0}>
            <span>
              {row.units}
              {row.unit_label ?? ''}
            </span>
            {/* 사용자당 과금이면 실제 사용자 수와 비교해 초과를 드러낸다 */}
            {row.billing_unit === 'PER_USER' && (
              <Text
                type={row.active_users > row.units ? 'danger' : 'secondary'}
                style={{ fontSize: 12 }}
              >
                사용 {row.active_users}명
              </Text>
            )}
          </Space>
        ),
    },
    {
      title: '상태',
      key: 'status',
      width: 150,
      /*
       * 「TRIALING」이라고만 찍으면 두 가지를 알 수 없다 — 우리말이 아니라는
       * 것과, **언제 끝나는지**다. 체험은 끝나는 날이 곧 매출이 시작되는
       * 날이라 남은 일수가 가장 중요한 정보다. 그래서 상태 자리에 함께 적는다.
       */
      render: (_, row) => {
        const left = row.status === 'TRIALING' ? trialDaysLeft(row.trial_ends_at) : null;

        if (left !== null) {
          const endsOn = row.trial_ends_at
            ? new Date(row.trial_ends_at).toLocaleDateString('ko-KR')
            : null;
          // 사흘 안쪽이면 곧 과금이 시작된다. 미리 눈에 띄어야 한다.
          const urgent = left <= 3;
          return (
            <Tooltip
              title={
                endsOn
                  ? `${endsOn} 까지 무료입니다. 그날까지는 청구서를 만들어도 금액이 0원입니다.`
                  : '체험 종료일이 적혀 있지 않습니다'
              }
            >
              <Tag color={urgent ? 'orange' : 'blue'}>
                {left === 0 ? '무료체험 오늘 종료' : `무료체험 D-${left}`}
              </Tag>
            </Tooltip>
          );
        }

        const meta: Record<string, { text: string; color: string; hint: string }> = {
          ACTIVE: { text: '이용중', color: 'green', hint: '정상 구독입니다' },
          TRIALING: { text: '무료체험', color: 'blue', hint: '체험 종료일이 적혀 있지 않습니다' },
          PAST_DUE: { text: '연체', color: 'red', hint: '청구서가 납기를 넘겼습니다' },
          GRACE: { text: '유예중', color: 'orange', hint: '연체 유예 기간입니다' },
          CANCELED: { text: '해지', color: 'default', hint: '해지된 구독입니다' },
          EXPIRED: { text: '종료', color: 'default', hint: '기간이 끝났습니다' },
          SUSPENDED: {
            text: '정지',
            color: 'volcano',
            hint: '운영자가 멈춘 구독입니다. 고객은 이 제품을 열 수 없고, 정지된 기간은 청구하지 않습니다.',
          },
        };
        const m = meta[row.status] ?? { text: row.status, color: 'default', hint: row.status };
        return (
          <Tooltip title={m.hint}>
            <Tag color={m.color}>{m.text}</Tag>
          </Tooltip>
        );
      },
    },
    {
      // 할인은 걸어 두면 보이지 않았다. 목록에 띄워야 "이 기업에 할인이
      // 걸려 있나" 를 묻지 않고 알 수 있고, 실수로 덮어쓰는 일이 줄어든다.
      title: '할인',
      key: 'discount',
      width: 130,
      render: (_, row) => {
        const list = row.discounts ?? [];
        if (list.length === 0) return <Text type="secondary">-</Text>;
        return (
          <Tooltip
            title={list
              .map((d) => `${discountLabel(d)} — ${d.reason}`)
              .join('\n')}
          >
            <Space size={4} wrap>
              {list.map((d) => (
                <Tag key={d.type} color="purple">
                  {discountLabel(d)}
                </Tag>
              ))}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: '연체',
      key: 'overdue',
      width: 120,
      render: (_, row) =>
        row.overdue_amount > 0 ? (
          <Text type="danger">{won(row.overdue_amount)}</Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: '관리',
      key: 'actions',
      width: 340,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4} wrap>
          <Button
            size="small"
            type="link"
            // 정지된 구독은 청구 대상이 아니다. 눌러 봐야 「청구할 금액이
            // 없다」가 나오므로 미리 막고 이유를 알려 준다.
            disabled={row.status === 'SUSPENDED'}
            onClick={() => void openPreview(row)}
          >
            청구서 발행
          </Button>
          <Button size="small" type="link" onClick={() => setUnitsTarget(row)}>
            수량
          </Button>
          <Button size="small" type="link" onClick={() => setPlanTarget(row)}>
            요금제
          </Button>
          <Button size="small" type="link" onClick={() => setDiscountTarget(row)}>
            할인
          </Button>
          <Button size="small" type="link" danger onClick={() => setStatusTarget(row)}>
            상태
          </Button>
        </Space>
      ),
    },
  ];

  const invoiceColumns: ColumnsType<InvoiceRow> = [
    { title: '청구서 번호', dataIndex: 'invoice_no', key: 'no', width: 170 },
    {
      title: '기업',
      key: 'company',
      render: (_, row) => (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: 'auto' }}
          onClick={() => setLedgerTenant(row.tenant_id)}
        >
          {row.company_name}
        </Button>
      ),
    },
    {
      title: '기간',
      key: 'period',
      width: 190,
      render: (_, row) => `${row.period_start?.slice(0, 10)} ~ ${row.period_end?.slice(0, 10)}`,
    },
    {
      /*
        발행일(= 생성일)을 보여 준다.
        「언제 나간 청구서인가」는 고객 문의의 첫 질문인데 목록에 없어서
        매번 상세를 열어야 했다. 이용 기간과 헷갈리지 않도록 바로 옆에 둔다 —
        이용 기간은 **쓴 기간**이고, 발행일은 **보낸 날**이다.

        자동으로 나갔는지도 함께 표시한다. 「자동으로 청구되고 있나」를
        목록에서 바로 확인할 수 있어야 한다.
      */
      title: '발행일',
      key: 'issued',
      width: 150,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text>{row.issued_at ? dayjs(row.issued_at).format('YYYY-MM-DD HH:mm') : '-'}</Text>
          <Tag color={row.issued_by_system ? 'blue' : 'default'} style={{ marginInlineEnd: 0 }}>
            {row.issued_by_system ? '자동' : '수동'}
          </Tag>
        </Space>
      ),
    },
    {
      title: '금액',
      key: 'amount',
      width: 170,
      align: 'right',
      render: (_, row) => (
        <Space direction="vertical" size={0} style={{ textAlign: 'right' }}>
          <strong>{won(row.total_amount)}</strong>
          {/*
            환불 차감은 **반드시 보여 준다.** 합계만 보이면 "왜 이 금액인가"를
            알 수 없고, 깎인 줄 모르고 다시 깎아 달라는 문의가 온다.
          */}
          {(row.credit_amount ?? 0) > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              환불 차감 −{won(row.credit_amount)}
            </Text>
          )}
          {row.paid_amount > 0 && row.paid_amount < row.total_amount && (
            <Text type="warning" style={{ fontSize: 12 }}>
              수납 {won(row.paid_amount)}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '납기',
      dataIndex: 'due_at',
      key: 'due',
      width: 120,
      render: (v: string | null) => (v ? new Date(v).toLocaleDateString('ko-KR') : '-'),
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v: string) => {
        const m = INVOICE_STATUS[v] ?? { text: v, color: 'default' };
        return <Tag color={m.color}>{m.text}</Tag>;
      },
    },
    {
      title: '관리',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, row) =>
        row.status === 'PAID' || row.status === 'VOID' ? (
          <Text type="secondary">-</Text>
        ) : (
          <Button
            size="small"
            type="link"
            onClick={() => {
              setPayTarget(row);
              payForm.setFieldsValue({
                amount: row.total_amount - row.paid_amount,
                method: 'TRANSFER',
              });
            }}
          >
            수납 기록
          </Button>
        ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Row gutter={16}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title={
                <Tooltip title="발행했는데 아직 다 안 들어온 금액. 부분 수납된 청구서는 남은 만큼만 셉니다.">
                  미수금
                </Tooltip>
              }
              value={summary.data?.outstanding_amount ?? 0}
              suffix="원"
              valueStyle={{ color: (summary.data?.outstanding_amount ?? 0) > 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="연체 금액"
              value={summary.data?.overdue_amount ?? 0}
              suffix="원"
              valueStyle={{ color: (summary.data?.overdue_amount ?? 0) > 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="연체 건수" value={summary.data?.overdue_count ?? 0} suffix="건" />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title={
                <Tooltip title="이번 달에 실제로 들어온 돈(입금일 기준, 환불 제외). 매출 현황의 「수납」과 같은 숫자입니다.">
                  이번 달 수납
                </Tooltip>
              }
              value={summary.data?.paid_this_month ?? 0}
              suffix="원"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      {(subscriptions.isError || invoices.isError) && (
        <Alert
          type="error"
          showIcon
          message={errorText((subscriptions.error ?? invoices.error) as NormalizedError)}
        />
      )}

      <Tabs
        tabBarExtraContent={
          <Space>
            <Button type="primary" onClick={() => setSubscribeOpen(true)}>
              신규 구독
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => void refresh()} />
          </Space>
        }
        items={[
          {
            key: 'subscriptions',
            label: '구독',
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/*
                  「유료 사용 시작일~종료일」로 찾으려면 어느 날짜인지 골라야
                  한다. 구독에는 시작일·체험 종료·다음 청구일이 모두 있어,
                  하나로 뭉뚱그리면 찾는 사람이 매번 다른 답을 본다.
                */}
                <Space wrap align="center">
                  <Select
                    value={subFilter.dateField}
                    onChange={(v) => setSubFilter((p) => ({ ...p, dateField: v }))}
                    style={{ width: 150 }}
                    options={[
                      { value: 'STARTED', label: '유료 시작일' },
                      { value: 'TRIAL_END', label: '체험 종료일' },
                      { value: 'NEXT_BILLING', label: '다음 청구일' },
                    ]}
                  />
                  <RangePicker
                    value={subFilter.range}
                    onChange={(v) =>
                      setSubFilter((p) => ({ ...p, range: (v as [Dayjs, Dayjs] | null) ?? null }))
                    }
                    allowEmpty={[true, true]}
                  />
                  <Input
                    placeholder="상호·식별자"
                    allowClear
                    style={{ width: 170 }}
                    value={subFilter.keyword}
                    onChange={(e) => setSubFilter((p) => ({ ...p, keyword: e.target.value }))}
                  />
                  <Select
                    placeholder="상태"
                    allowClear
                    style={{ width: 130 }}
                    value={subFilter.status}
                    onChange={(v) => setSubFilter((p) => ({ ...p, status: v }))}
                    options={[
                      { value: 'ACTIVE', label: '이용중' },
                      { value: 'TRIALING', label: '무료체험' },
                      { value: 'PAST_DUE', label: '연체' },
                      { value: 'GRACE', label: '유예중' },
                      // 정지된 구독은 청구하지 않는다. 걸러 볼 수 없으면
                      // "왜 이 기업은 청구서가 안 나오나" 를 알 길이 없다.
                      { value: 'SUSPENDED', label: '정지' },
                      { value: 'CANCELED', label: '해지' },
                      { value: 'EXPIRED', label: '종료' },
                    ]}
                  />
                  <Checkbox
                    checked={subFilter.overdueOnly}
                    onChange={(e) => setSubFilter((p) => ({ ...p, overdueOnly: e.target.checked }))}
                  >
                    연체만
                  </Checkbox>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() =>
                      downloadCsv('구독현황', subscriptions.data ?? [], [
                        { header: '기업', value: (r) => r.company_name },
                        { header: '식별자', value: (r) => r.slug },
                        { header: '제품', value: (r) => r.product_name },
                        { header: '요금제', value: (r) => r.plan_name },
                        { header: '상태', value: (r) => r.status },
                        { header: '계약수량', value: (r) => r.units },
                        { header: '사용자수', value: (r) => r.active_users },
                        { header: '월금액', value: (r) => Math.round(r.monthly_amount ?? 0) },
                        { header: '체험종료', value: (r) => csvDate(r.trial_ends_at) },
                        { header: '다음청구', value: (r) => csvDate(r.current_period_end) },
                        { header: '연체금액', value: (r) => Math.round(r.overdue_amount) },
                      ])
                    }
                  >
                    엑셀 내보내기
                  </Button>
                </Space>

                <Table<SubscriptionRow>
                  rowKey="subscription_id"
                  columns={subscriptionColumns}
                  dataSource={subscriptions.data ?? []}
                  loading={subscriptions.isLoading}
                  size="middle"
                  scroll={{ x: 1000 }}
                  pagination={false}
                  locale={{ emptyText: '조건에 맞는 구독이 없습니다.' }}
                />
              </Space>
            ),
          },
          {
            key: 'invoices',
            label: '청구서',
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space wrap align="center">
                  <Select
                    value={invFilter.dateField}
                    onChange={(v) => setInvFilter((p) => ({ ...p, dateField: v }))}
                    style={{ width: 130 }}
                    options={[
                      { value: 'PERIOD', label: '이용 기간' },
                      { value: 'ISSUED', label: '발행일' },
                      { value: 'DUE', label: '납기일' },
                    ]}
                  />
                  <RangePicker
                    value={invFilter.range}
                    onChange={(v) =>
                      setInvFilter((p) => ({ ...p, range: (v as [Dayjs, Dayjs] | null) ?? null }))
                    }
                    allowEmpty={[true, true]}
                  />
                  <Input
                    placeholder="상호·청구서 번호"
                    allowClear
                    style={{ width: 190 }}
                    value={invFilter.keyword}
                    onChange={(e) => setInvFilter((p) => ({ ...p, keyword: e.target.value }))}
                  />
                  <Select
                    placeholder="상태"
                    allowClear
                    style={{ width: 120 }}
                    value={invFilter.status}
                    onChange={(v) => setInvFilter((p) => ({ ...p, status: v }))}
                    options={[
                      { value: 'ISSUED', label: '발행' },
                      { value: 'PARTIALLY_PAID', label: '부분수납' },
                      { value: 'PAID', label: '완납' },
                      { value: 'OVERDUE', label: '연체' },
                      { value: 'VOID', label: '무효' },
                    ]}
                  />
                  <Checkbox
                    checked={invFilter.unpaidOnly}
                    onChange={(e) => setInvFilter((p) => ({ ...p, unpaidOnly: e.target.checked }))}
                  >
                    미납만
                  </Checkbox>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() =>
                      downloadCsv('청구서', invoices.data ?? [], [
                        { header: '청구서번호', value: (r) => r.invoice_no },
                        { header: '기업', value: (r) => r.company_name },
                        { header: '식별자', value: (r) => r.slug },
                        { header: '상태', value: (r) => r.status },
                        { header: '이용시작', value: (r) => csvDate(r.period_start) },
                        { header: '이용종료', value: (r) => csvDate(r.period_end) },
                        { header: '발행일', value: (r) => csvDate(r.issued_at) },
                        { header: '납기일', value: (r) => csvDate(r.due_at) },
                        { header: '공급가액', value: (r) => Math.round(r.subtotal_amount) },
                        { header: '부가세', value: (r) => Math.round(r.vat_amount) },
                        { header: '환불차감', value: (r) => Math.round(r.credit_amount ?? 0) },
                        { header: '합계', value: (r) => Math.round(r.total_amount) },
                        { header: '수납액', value: (r) => Math.round(r.paid_amount) },
                        {
                          header: '미납액',
                          value: (r) => Math.round(r.total_amount - r.paid_amount),
                        },
                        { header: '자동발행', value: (r) => (r.issued_by_system ? 'Y' : 'N') },
                      ])
                    }
                  >
                    엑셀 내보내기
                  </Button>
                </Space>

                <Table<InvoiceRow>
                  rowKey="invoice_id"
                  columns={invoiceColumns}
                  dataSource={invoices.data ?? []}
                  loading={invoices.isLoading}
                  size="middle"
                  /* 발행일 칸이 늘었다. 좁히면 날짜가 두 줄로 접힌다 */
                  scroll={{ x: 1280 }}
                  pagination={{ pageSize: 20, showTotal: (t) => `총 ${t}건` }}
                  locale={{ emptyText: '조건에 맞는 청구서가 없습니다.' }}
                />
              </Space>
            ),
          },
          {
            /*
              청구서를 기업 하나씩 내보내던 일을 한 화면으로 옮긴다.
              기업마다 자동/수동을 여기서 바로 바꿀 수 있다.
            */
            key: 'batch',
            label: '일괄 청구',
            children: <BatchInvoicePanel onOpenLedger={setLedgerTenant} />,
          },
          {
            /*
              환불은 기록만 되고 볼 곳이 없었다. 「아직 못 갚은 금액」이
              이 탭의 핵심이다 — 환불은 계좌로 보내지 않고 다음 청구서에서
              깎아 갚기 때문이다.
            */
            key: 'refunds',
            label: '환불',
            children: <RefundPanel onOpenLedger={setLedgerTenant} />,
          },
          {
            /*
              계좌이체로 받는 기업에는 세금계산서를 끊어 주어야 한다.
              홈택스 엑셀 일괄 등록 양식을 여기서 만들어 내려받는다.
            */
            key: 'tax',
            label: '세금계산서',
            children: <TaxInvoicePanel onOpenLedger={setLedgerTenant} />,
          },
        ]}
      />

      {/* 기업 이름을 누르면 그 기업의 청구·수납·환불이 시간순으로 열린다 */}
      <TenantLedgerDrawer tenantId={ledgerTenant} onClose={() => setLedgerTenant(null)} />

      {/* 청구서 발행 — 금액을 먼저 보여준다 */}
      <Modal
        open={Boolean(previewTarget)}
        title={`청구서 발행 — ${previewTarget?.company_name ?? ''}`}
        okText="발행"
        cancelText="취소"
        confirmLoading={issuing}
        onCancel={() => setPreviewTarget(null)}
        onOk={issue}
        /* 이미 나간 달이면 단추 자체를 막는다. 눌러 봐야 거절된다 */
        okButtonProps={{ disabled: !preview || Boolean(preview.existingInvoice) }}
        width={680}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* 어느 달을 청구하는지 고르게 한다. 기본값만으로는 늘 지난달이다. */}
          <Space align="center">
            <Text strong>청구 대상 월</Text>
            <DatePicker
              picker="month"
              value={billingMonth}
              onChange={(v) => void changeMonth(v)}
              allowClear={false}
              format="YYYY년 M월"
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {billingMonth.startOf('month').format('YYYY-MM-DD')} ~{' '}
              {billingMonth.endOf('month').format('YYYY-MM-DD')}
            </Text>
          </Space>

          {/*
            체험 중이면 금액이 0원이거나 계산 자체가 거절된다.
            발행 버튼을 누르기 전에 이유를 먼저 보여 준다.
          */}
          {previewTarget &&
            previewTarget.status === 'TRIALING' &&
            (() => {
              const left = trialDaysLeft(previewTarget.trial_ends_at);
              const endsOn = previewTarget.trial_ends_at
                ? new Date(previewTarget.trial_ends_at).toLocaleDateString('ko-KR')
                : null;
              return (
                <Alert
                  type="warning"
                  showIcon
                  message={
                    left === null
                      ? '무료 체험 중인 구독입니다'
                      : left === 0
                        ? '무료 체험이 오늘 끝납니다'
                        : `무료 체험 중입니다 — ${left}일 남았습니다`
                  }
                  description={
                    <>
                      {endsOn && (
                        <>
                          체험은 <strong>{endsOn}</strong> 까지입니다.{' '}
                        </>
                      )}
                      체험이 <strong>그 달 끝까지 이어지는 기간</strong>은 청구하지 않습니다. 첫
                      청구는 체험이 끝난 달부터 하시면 됩니다.
                    </>
                  }
                />
              );
            })()}

          {/*
            이미 발행된 달인지 **누르기 전에** 알려 준다.
            예전에는 금액만 보여 주고 [발행] 을 눌러야 「겹치는 청구서가
            있습니다」로 막혔다. 운영자는 그제야 달을 잘못 골랐다는 것을 안다.
          */}
          {preview?.existingInvoice && (
            <Alert
              type="warning"
              showIcon
              message={`이 기간은 이미 청구서가 나갔습니다 — ${preview.existingInvoice.invoiceNo}`}
              description={
                <>
                  {preview.existingInvoice.periodStart} ~ {preview.existingInvoice.periodEnd} ·{' '}
                  {won(preview.existingInvoice.totalAmount)} ·{' '}
                  {INVOICE_STATUS[preview.existingInvoice.status]?.text ??
                    preview.existingInvoice.status}
                  {preview.existingInvoice.issuedAt &&
                    ` · ${dayjs(preview.existingInvoice.issuedAt).format('YYYY-MM-DD')} 발행`}
                  <br />
                  <b>지금 누르면 거절됩니다.</b> 다른 달을 고르시거나, 다시 발행해야 한다면
                  그 청구서를 먼저 무효화해 주세요.
                </>
              }
            />
          )}

          {previewError ? (
            <Alert
              type="error"
              showIcon
              message="이 기간은 청구할 금액이 없습니다"
              description={
                <>
                  {previewError}
                  <br />
                  위의 <strong>청구 대상 월</strong>을 바꿔 다시 확인해 보세요.
                </>
              }
            />
          ) : !preview ? (
            <Text type="secondary">금액을 계산하는 중입니다…</Text>
          ) : (
            <>
            <Alert
              type="info"
              showIcon
              message={`청구 기간: ${preview.periodStart} ~ ${preview.periodEnd}`}
              description="발행하면 고객사 화면에 즉시 보입니다. 같은 기간에 두 번 발행되지는 않습니다."
            />

            <Table<PreviewLine>
              rowKey={(r) => `${r.itemType}-${r.description}`}
              size="small"
              pagination={false}
              dataSource={preview.lines}
              columns={[
                { title: '항목', dataIndex: 'description', key: 'd' },
                {
                  title: '수량',
                  dataIndex: 'quantity',
                  key: 'q',
                  width: 90,
                  align: 'right',
                  render: (v: number) => v.toLocaleString('ko-KR'),
                },
                {
                  title: '금액',
                  dataIndex: 'amount',
                  key: 'a',
                  width: 130,
                  align: 'right',
                  render: (v: number) => (
                    <Text type={v < 0 ? 'success' : undefined}>{won(v)}</Text>
                  ),
                },
              ]}
            />

            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="공급가액">
                {won(preview.subtotalAmount - preview.discountAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="부가세">{won(preview.vatAmount)}</Descriptions.Item>
              {/*
                환불 차감은 부가세 **다음**에 온다. 공급가에서 빼면 세액이
                함께 줄어 세금계산서와 어긋나기 때문이다 — 환불은 지난
                거래의 수정이지 이번 달 매출의 감소가 아니다.
              */}
              {(preview.creditAmount ?? 0) > 0 && (
                <Descriptions.Item label="환불 차감">
                  <Text type="success">−{won(preview.creditAmount)}</Text>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                    고객에게 돌려드려야 할 환불을 이번 청구서에서 깎습니다.
                  </div>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="합계">
                <strong style={{ fontSize: 16 }}>{won(preview.totalAmount)}</strong>
              </Descriptions.Item>
            </Descriptions>
            </>
          )}
        </Space>
      </Modal>

      {/* 수납 기록 */}
      <Modal
        open={Boolean(payTarget)}
        title={`수납 기록 — ${payTarget?.invoice_no ?? ''}`}
        okText="기록"
        cancelText="취소"
        onCancel={() => setPayTarget(null)}
        onOk={recordPayment}
        width={520}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="기업">{payTarget?.company_name}</Descriptions.Item>
            <Descriptions.Item label="청구액">{won(payTarget?.total_amount)}</Descriptions.Item>
            <Descriptions.Item label="기수납">{won(payTarget?.paid_amount)}</Descriptions.Item>
            <Descriptions.Item label="미수">
              <Text type="danger">
                {won((payTarget?.total_amount ?? 0) - (payTarget?.paid_amount ?? 0))}
              </Text>
            </Descriptions.Item>
          </Descriptions>

          <Form form={payForm} layout="vertical" requiredMark={false}>
            <Form.Item
              name="amount"
              label="수납 금액"
              rules={[{ required: true, message: '금액을 입력하세요' }]}
            >
              {/* 값 타입을 number 로 고정한다. 지정하지 않으면 antd 가 min 의
                  리터럴 타입(1)으로 추론해 parser 반환형과 충돌한다. */}
              <InputNumber<number>
                min={1}
                style={{ width: '100%' }}
                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(v) => Number((v ?? '').replace(/,/g, ''))}
                addonAfter="원"
              />
            </Form.Item>
            <Form.Item name="method" label="수납 방법">
              <Select
                options={[
                  { value: 'TRANSFER', label: '계좌이체' },
                  { value: 'CARD', label: '카드' },
                  { value: 'MANUAL', label: '기타(수기)' },
                ]}
              />
            </Form.Item>
            {/*
              입금일을 받는다. 비우면 오늘로 기록된다.

              이 값이 **매출의 「입금액」이 잡히는 달**을 정한다. 10월 31일에
              들어온 돈을 11월 3일에 기록하면서 오늘로 남기면 11월 매출이
              된다. 월말 입금은 흔하고, 어긋난 월은 세무 자료와 맞지 않는다.
            */}
            <Form.Item
              name="paidOn"
              label="입금일"
              extra="통장에 찍힌 날짜입니다. 비우면 오늘로 기록됩니다."
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder="오늘"
                // 아직 오지 않은 날짜로 입금을 적으면 그 달 매출이 부풀어 오른다.
                disabledDate={(d) => d && d > dayjs().endOf('day')}
              />
            </Form.Item>
            {/*
              입금자명과 거래번호를 나눈다. 예전에는 한 칸에 섞어 적었다.
              상호와 입금자명이 다른 경우(대표자 개인 명의, 모회사 명의)가
              흔해서, 통장에 찍힌 이름을 그대로 남겨 두어야 나중에 "이 돈이
              어느 회사 것인가" 를 맞출 수 있다.
            */}
            <Form.Item name="payerName" label="입금자명">
              <Input placeholder="통장에 찍힌 이름 (예: 홍길동, (주)에이콤)" maxLength={100} />
            </Form.Item>
            <Form.Item name="reference" label="거래번호">
              <Input placeholder="은행 거래번호 등 (선택)" maxLength={200} />
            </Form.Item>
            <Form.Item name="memo" label="메모">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      {/* 요금제 변경 */}
      <Modal
        open={Boolean(planTarget)}
        title={`요금제 변경 — ${planTarget?.company_name ?? ''} · ${planTarget?.product_name ?? ''}`}
        okText="적용"
        cancelText="취소"
        onCancel={() => setPlanTarget(null)}
        onOk={changePlan}
        width={600}
        destroyOnHidden
        afterOpenChange={(open) => {
          if (open) {
            planForm.setFieldsValue({
              planId: undefined,
              units: planTarget?.units,
              reason: '',
            });
          }
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="바꾼 날부터 새 요금입니다"
            description={
              `지금은 「${planTarget?.plan_name ?? ''}」 입니다. 오늘 바꾸면 이번 달 청구서에 ` +
              '옛 요금제와 새 요금제가 날짜로 나뉘어 두 줄로 나옵니다 — ' +
              '쓰지 않은 날의 요금을 받지 않습니다. 이미 발행한 청구서는 바뀌지 않습니다.'
            }
          />
          <Form form={planForm} layout="vertical" requiredMark={false}>
            <Form.Item
              name="planId"
              label="바꿀 요금제"
              rules={[{ required: true, message: '요금제를 고르세요' }]}
              extra="같은 제품의 판매 중인 요금제만 나옵니다."
            >
              <Select
                loading={plans.isLoading}
                options={plansFor(planTarget?.product_code)}
                placeholder="요금제 선택"
                notFoundContent="고를 수 있는 요금제가 없습니다"
              />
            </Form.Item>
            <Form.Item
              name="units"
              label="계약 수량"
              rules={[{ required: true, message: '수량을 입력하세요' }]}
              extra="비워 두면 지금 수량을 그대로 씁니다. 새 요금제의 최소·최대 수량을 벗어나면 거절됩니다."
            >
              <InputNumber min={1} max={100000} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="reason"
              label="사유"
              rules={[{ required: true, message: '사유를 입력하세요' }]}
            >
              <Input.TextArea rows={2} placeholder="예: 상위 요금제로 전환 (견적서 #2026-031)" />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      {/* 신규 구독 */}
      <Modal
        open={subscribeOpen}
        title="신규 구독"
        okText="구독 시작"
        cancelText="취소"
        onCancel={() => setSubscribeOpen(false)}
        onOk={subscribeProduct}
        width={600}
        destroyOnHidden
        afterOpenChange={(open) => {
          if (open) subscribeForm.setFieldsValue({ tenantId: undefined, planId: undefined, units: undefined, reason: '' });
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="요금제가 제품을 정합니다"
            description={
              '고른 요금제의 제품으로 구독이 만들어집니다. 이미 그 제품을 구독 중이면 거절되니, ' +
              '그때는 「요금제」 단추로 바꾸세요. 요금제에 무료 체험 일수가 있으면 체험으로 시작합니다.'
            }
          />
          <Form form={subscribeForm} layout="vertical" requiredMark={false}>
            <Form.Item
              name="tenantId"
              label="기업"
              rules={[{ required: true, message: '기업을 고르세요' }]}
            >
              <Select
                showSearch
                loading={tenantOptions.isLoading}
                placeholder="기업 선택"
                optionFilterProp="label"
                // 플랫폼 운영 기업(system)은 고를 수 없다. 우리 자신에게
                // 요금을 매길 일은 없는데 목록 끝에 같이 떠서 잘못 고르기 쉽다.
                options={(tenantOptions.data ?? [])
                  .filter((t) => t.slug !== 'system')
                  .map((t) => ({
                    value: t.tenantId,
                    label: `${t.companyName} (${t.slug})`,
                  }))}
              />
            </Form.Item>
            <Form.Item
              name="planId"
              label="요금제"
              rules={[{ required: true, message: '요금제를 고르세요' }]}
            >
              <Select
                showSearch
                loading={plans.isLoading}
                placeholder="요금제 선택"
                optionFilterProp="label"
                options={(plans.data ?? [])
                  .filter((p) => p.is_active)
                  .map((p) => ({
                    value: p.plan_id,
                    label: `[${p.product_name}] ${p.plan_name} — 기본 ${won(p.base_price)}${
                      p.billing_unit === 'FLAT'
                        ? ' (정액)'
                        : ` + ${p.unit_label ?? '단위'}당 ${won(p.unit_price)}`
                    }`,
                  }))}
              />
            </Form.Item>
            <Form.Item
              name="units"
              label="계약 수량"
              extra="비워 두면 요금제의 최소 수량으로 시작합니다."
            >
              <InputNumber min={1} max={100000} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="reason"
              label="사유"
              rules={[{ required: true, message: '사유를 입력하세요' }]}
            >
              <Input.TextArea rows={2} placeholder="예: 신규 계약 (계약서 #2026-033)" />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      {/* 계약 수량 */}
      <Modal
        open={Boolean(unitsTarget)}
        title={`계약 수량 — ${unitsTarget?.company_name ?? ''} · ${unitsTarget?.product_name ?? ''}`}
        okText="적용"
        cancelText="취소"
        onCancel={() => setUnitsTarget(null)}
        onOk={changeUnits}
        width={520}
        destroyOnHidden
        afterOpenChange={(open) => {
          if (open) unitsForm.setFieldsValue({ units: unitsTarget?.units, reason: '' });
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="청구 금액이 바로 달라집니다"
            description={
              `청구액은 계약 수량으로 계산합니다 — 실제 사용자 수가 아닙니다. ` +
              `지금 ${unitsTarget?.units ?? 0}${unitsTarget?.unit_label ?? '명'} 계약에 ` +
              `실사용 ${unitsTarget?.active_users ?? 0}명입니다. ` +
              '사용자당 과금이면 실제 사용자보다 적게 줄일 수 없습니다 — 줄이는 순간 누군가 로그인하지 못하게 됩니다. ' +
              '아직 발행하지 않은 이번 기간부터 적용됩니다.'
            }
          />
          <Form form={unitsForm} layout="vertical" requiredMark={false}>
            <Form.Item
              name="units"
              label={`계약 수량 (${unitsTarget?.unit_label ?? '사용자'})`}
              rules={[{ required: true, message: '수량을 입력하세요' }]}
              extra={
                unitsTarget && unitsTarget.billing_unit !== 'FLAT'
                  ? `${unitsTarget.unit_label ?? '단위'}당 ${won(unitsTarget.unit_price)} · 기본 포함 ${unitsTarget.included_units}`
                  : '정액 요금제라 수량을 바꿔도 금액은 그대로입니다.'
              }
            >
              <InputNumber min={1} max={100000} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="reason"
              label="사유"
              rules={[{ required: true, message: '사유를 입력하세요' }]}
            >
              <Input.TextArea rows={2} placeholder="예: 증원 요청 (메일 2026-10-26)" />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      {/* 구독 상태 */}
      <Modal
        open={Boolean(statusTarget)}
        title={`구독 상태 — ${statusTarget?.company_name ?? ''} · ${statusTarget?.product_name ?? ''}`}
        okText="적용"
        cancelText="취소"
        okButtonProps={{ danger: true }}
        onCancel={() => setStatusTarget(null)}
        onOk={changeStatus}
        width={520}
        destroyOnHidden
        afterOpenChange={(open) => {
          if (open) {
            statusForm.setFieldsValue({
              status: statusTarget?.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED',
              reason: '',
              cancelAtPeriodEnd: true,
            });
          }
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="고객이 바로 영향을 받습니다"
            description={
              '정지하면 고객은 그 제품을 열 수 없고, 정지된 기간은 청구하지 않습니다. ' +
              '해지는 되돌릴 수 없습니다 — 「기간 말 해지」로 예약하면 이미 낸 기간까지는 그대로 씁니다. ' +
              '미납으로 멈추는 것은 이것과 별개로 배치가 처리합니다.'
            }
          />
          <Form form={statusForm} layout="vertical" requiredMark={false}>
            <Form.Item name="status" label="바꿀 상태">
              <Select
                options={[
                  { value: 'ACTIVE', label: '이용중 — 정지를 풀고 다시 쓰게 합니다' },
                  { value: 'SUSPENDED', label: '정지 — 못 쓰게 막고, 그 기간은 청구하지 않습니다' },
                  { value: 'CANCELED', label: '해지 — 구독을 끝냅니다' },
                ]}
              />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, next) => prev.status !== next.status}
            >
              {({ getFieldValue }) =>
                getFieldValue('status') === 'CANCELED' ? (
                  <Form.Item name="cancelAtPeriodEnd" valuePropName="checked">
                    <Checkbox>
                      기간 말에 해지 (지금 끊지 않고 이번 주기가 끝나면 종료)
                    </Checkbox>
                  </Form.Item>
                ) : null
              }
            </Form.Item>
            <Form.Item
              name="reason"
              label="사유"
              rules={[{ required: true, message: '사유를 입력하세요' }]}
            >
              <Input.TextArea rows={2} placeholder="예: 고객 요청으로 ECO 일시 중지 (티켓 #1042)" />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      {/* 할인 설정 */}
      <Modal
        open={Boolean(discountTarget)}
        title={`할인 설정 — ${discountTarget?.company_name ?? ''} · ${discountTarget?.product_name ?? ''}`}
        okText="적용"
        cancelText="취소"
        onCancel={() => setDiscountTarget(null)}
        onOk={setDiscount}
        width={520}
        destroyOnHidden
        afterOpenChange={(open) => {
          if (!open) return;
          /*
           * 지금 걸려 있는 할인을 **먼저 채운다.**
           *
           * 예전에는 언제나 「비율 10%」로 초기화했다. 20% 할인이 걸린 기업에
           * 창을 열면 10% 가 떠 있고, 사유만 적고 적용하면 **할인이 조용히
           * 반토막** 난다. 바꿀 생각이 없었는데 바뀐다.
           */
          const current = discountTarget?.discounts?.[0];
          discountForm.setFieldsValue(
            current
              ? {
                  type: current.type,
                  value:
                    current.type === 'DISCOUNT_RATE'
                      ? Number((current.value * 100).toFixed(2))
                      : current.value,
                  reason: '',
                }
              : { type: 'DISCOUNT_RATE', value: 10, reason: '' },
          );
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {(discountTarget?.discounts?.length ?? 0) > 0 && (
            <Alert
              type="info"
              showIcon
              message="지금 걸려 있는 할인"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {discountTarget?.discounts?.map((d) => (
                    <li key={d.type}>
                      {d.type === 'DISCOUNT_RATE' ? '비율' : '정액'} {discountLabel(d)} —{' '}
                      {d.reason}
                    </li>
                  ))}
                </ul>
              }
            />
          )}
          <Alert
            type="warning"
            showIcon
            message="매출에 직접 영향을 주는 조작입니다"
            description={
              `할인은 ${discountTarget?.product_name ?? '이 제품'} 구독에만 적용되며, ` +
              '그 제품이 낼 금액(기본료·수량 요금·초과 사용료)을 넘지 않습니다. ' +
              '승인자와 사유가 감사로그에 남고, 같은 방식의 기존 할인은 자동으로 종료됩니다. ' +
              '아직 발행하지 않은 이번 기간부터 적용되고, 이미 발행한 청구서는 바뀌지 않습니다.'
            }
          />
          <Form form={discountForm} layout="vertical" requiredMark={false}>
            <Form.Item name="type" label="할인 방식">
              <Select
                options={[
                  { value: 'DISCOUNT_RATE', label: '비율 할인 (%)' },
                  { value: 'DISCOUNT_AMOUNT', label: '정액 할인 (원)' },
                ]}
              />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, next) => prev.type !== next.type}
            >
              {({ getFieldValue }) => {
                const isRate = getFieldValue('type') === 'DISCOUNT_RATE';
                return (
                  <Form.Item
                    name="value"
                    label={isRate ? '할인율 (%)' : '할인 금액 (원)'}
                    rules={[
                      { required: true, message: '값을 입력하세요' },
                      // 비율은 100% 를 넘을 수 없다. 넘겨 보내면 서버가 거절하는데,
                      // 그때 「0~1 사이」라는 저장 단위로 답이 와서 운영자가
                      // 0.1 을 넣고 10% 를 기대하게 된다(실제로는 0.1%).
                      {
                        type: 'number',
                        max: isRate ? 100 : undefined,
                        message: '할인율은 100% 를 넘을 수 없습니다',
                      },
                    ]}
                    extra={
                      isRate
                        ? '0~100 사이의 %로 입력합니다 (10 = 10%). 0을 넣으면 할인이 해제됩니다.'
                        : '원 단위로 입력합니다. 그 제품의 청구액보다 크면 청구액까지만 적용됩니다. 0을 넣으면 할인이 해제됩니다.'
                    }
                  >
                    <InputNumber
                      min={0}
                      max={isRate ? 100 : undefined}
                      style={{ width: '100%' }}
                      addonAfter={isRate ? '%' : '원'}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
            <Form.Item
              name="reason"
              label="사유"
              rules={[{ required: true, message: '사유를 입력하세요' }]}
            >
              <Input.TextArea rows={2} placeholder="예: 연간 선결제 계약 (계약서 #2026-014)" />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
        결제 대행사(PG) 연동 전이라 수납은 입금 확인 후 수기로 기록합니다.
        할인·환불은 승인자와 사유가 감사로그에 남습니다.
      </Paragraph>
    </Space>
  );
}
